/**
 * Sprint 15 Ola 7 — AuriaFAB
 * Floating Action Button for aurIA conversacional.
 * Replaces PLAriaFAB. Reads state from useAuriaContext.
 */

import React from 'react'
import { useLocation } from 'react-router-dom'
import { useAuriaContext } from './AuriaContextProvider'
import AuriaIcon from './AuriaIcon'

const pulseStyle = `
@keyframes auria-fab-pulse {
  0% { box-shadow: 0 0 0 0 rgba(192, 113, 90, 0.5); }
  70% { box-shadow: 0 0 0 10px rgba(192, 113, 90, 0); }
  100% { box-shadow: 0 0 0 0 rgba(192, 113, 90, 0); }
}
.auria-fab-pulse {
  animation: auria-fab-pulse 2s ease-in-out infinite;
}
`

let fabStylesInjected = false
function injectFabStyles() {
  if (fabStylesInjected || typeof document === 'undefined') return
  const tag = document.createElement('style')
  tag.textContent = pulseStyle
  document.head.appendChild(tag)
  fabStylesInjected = true
}

export default function AuriaFAB() {
  injectFabStyles()

  const { isOpen, openSheet, pendingContext } = useAuriaContext()
  const location = useLocation()

  // Hidden when sheet is open
  if (isOpen) return null

  // Hidden on /aria route (legacy compat)
  if (location.pathname === '/aria') return null

  const hasPending = !!pendingContext

  return (
    <button
      data-testid="auria-fab"
      onClick={openSheet}
      className={hasPending ? 'auria-fab-pulse' : ''}
      aria-label="Abrir aurIA"
      style={{
        position: 'fixed',
        /**
         * COLA:el-fab-de-auria-tapa-la-pestana-de-progreso · EL FAB TIENE QUE DEJAR LIBRE LA BARRA DE ABAJO.
         *
         * 🔴 Aquí había `bottom: 24`, un número a mano, y la barra de pestañas vive a `bottom: 12px` con **54 px** de alto:
         * el FAB (56 px) caía **encima**. Medido en el bundle servido a 375, 393 y 440 con el medidor del juez: la pestaña
         * **«Progreso»** (x 286, ancho 66) aparece en `tapados`, tapada por **«Abrir aurIA»** (x 295, ancho 56), en los
         * **tres** anchos y en los **tres** estados de la pantalla. O sea: quien toca «Progreso» en Perfil abre aurIA.
         *
         * Y no era un fallo de cálculo, era un número suelto: el FAB del sistema visual (`design-system/actions/FabAuria`)
         * usa esta misma expresión y por eso en Today queda **a 72 px, por encima de la barra**. Dos FAB con dos criterios
         * para el mismo hueco. Aquí se usa el criterio del sistema; que haya **tres** FAB de aurIA en el repo queda dicho
         * en la cola, no se arregla de paso.
         *
         * Alcance: todo lo que monta `AppLayout` — **Perfil**, **Revisión** y **Lo que no entra**, y las dos últimas son
         * justo las que Perfil enlaza en «Tu ficha».
         */
        bottom: 'calc(var(--pl-bottom-nav-height) + var(--pl-safe-bottom) + var(--pl-space-3))',
        right: 24,
        zIndex: 100,
        width: 56,
        height: 56,
        borderRadius: '50%',
        border: 'none',
        background: 'linear-gradient(135deg, var(--prime-terracota, #c0715a), var(--prime-sage, #7d9e8c))',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
    >
      <AuriaIcon variant="default" size={28} />
    </button>
  )
}
